import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  ListItemIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Modal,
  Typography,
  Tooltip
} from "@mui/material";
import * as Yup from "yup";
import "./CropStage.css";
import Layout from "components/structure/Layout";
import React, { useContext, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { SnackbarContext } from "context/snackBarContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CheckboxTick from "assets/CheckboxTick.svg";
import CheckboxUntick from "assets/CheckboxUntick.svg";
import CustomButton from "utils/CustomButton";
import { formatDateDetails } from "utils/utils";
import CropStageService from "services/CropStageService";
import { AuthData } from "auth/AuthWarpper";
import dangerIcon from "assets/danger.svg";
import UncheckedDisabled from "assets/UncheckedDisabled.svg";


const validationSchema = Yup.object({
  cropStageName: Yup.string()
    .required("Crop Stage Name is required")
    .max(50, "Crop Stage must be less than 50 characters")
    .test(
      "no-blank-spaces",
      "Crop Stage Name is required and cannot consist only of blank spaces",
      (value) => value.trim().length > 0
    ),
  cropStageCountryDetail: Yup.array()
    .min(1, "At least one item is required")
    .required("Country is required"),
});

const AddCropStage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const readOnly = location.state?.readOnly ?? true;
  const { user } = AuthData();
  const { id } = useParams();

  const { state } = location;
  const {
    title,
    status,
    countryArr,
    code,
    compliance,
    modifiedTime,
    modifiedBy,
    sequence,
    edit,
    isMultipleSurveys,
    isSubGrowerMonitorings
  } = state || {};
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selected, setSelected] = useState([]);
  const [formData, setFormData] = useState({
    cropStageName: title || "",
    countryCode: code || "",
    isActive: status || false,
    visitCompliance: compliance || false,
    isMultipleSurvey: isMultipleSurveys || false, // Add this new field
    isWoodMonitoring: state?.isWoodMonitorings || false, // Add this new field
    isSubGrowerMonitoring: isSubGrowerMonitorings || false,
    cropStageCountryDetail: selected,
  });
  const [cropStageDataWithDetails, setCropStageDataWithDetails] = useState({});
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    const val =
      (name === "isActive" ||
        name === "visitCompliance" ||
        name === "isMultipleSurvey" ||
        name === "isWoodMonitoring" ||
        name === "isSubGrowerMonitoring") ? checked : value;

    setFormData({
      ...formData,
      [name]: val,
    });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const response = await CropStageService.getCountryList();
        if (response.status === 200) {
          const data = response.data;
          // Concatenate the fetched countries with the existing countries array

          if (edit === true) {
            setCountries([
              ...countryArr,
              ...data.response.filter(
                (country) =>
                  !countryArr.some(
                    (existingCountry) =>
                      existingCountry.countryCode === country.countryCode
                  )
              ),
            ]);

            // Initialize selected countries with countryArr
            const selectedCountries = countryArr.filter((country) =>
              data.response.some(
                (selectedCountry) =>
                  selectedCountry.countryCode === country.countryCode
              )
            );
            setSelected(selectedCountries);

            setFormData({
              ...formData,
              cropStageCountryDetail: selectedCountries,
            });
          } else {
            setCountries(data.response);
            setSelected(data.response);
            setFormData({
              ...formData,
              cropStageCountryDetail: data.response,
            });
          }
        } else {
          console.error("Failed to fetch countries");
        }
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    async function fetchCropStageDetails() {
      if (edit && id) {
        try {
          setLoading(true);
          const response = await CropStageService.getCropStageDetails(id);

          if (response.status === 200) {
            const cropStageData = response.data.response;
            setCropStageDataWithDetails(cropStageData);

          } else {
            setSeverity("error");
            setMessage("Failed to fetch crop stage details");
            setOpenAlert(true);
          }
        } catch (error) {
          setSeverity("error");
          setMessage(error?.response?.data?.message || "Failed to fetch crop stage details");
          setOpenAlert(true);
        } finally {
          setLoading(false);
        }
      }
    }

    fetchCropStageDetails();
  }, [edit, id]);

  const isAllSelected =
    countries.length > 0 && selected.length === countries.length;

  const handleChangeCountry = (event) => {
    const option = event.target.value;

    if (option[option.length - 1] === "all") {
      const isUnselectingAll = selected.length === countries.length;

      if (isUnselectingAll) {
        // Check if any selected country has isSafeToRemove = false
        const unsafeCountries = selected.filter(country => {
          const countryDetails = cropStageDataWithDetails?.cropStageCountryDetail?.find(
            detail => detail.countryCode === country.countryCode
          );
          return countryDetails?.isSafeToRemove === false;
        });

        if (unsafeCountries.length > 0) {
          setSeverity("error");
          setMessage(`This country is associated with one or more modules. Please 
deselect the country from the modules first, then try again`);
          setOpenAlert(true);
          return;
        }
      }

      // If we reach here, either we're selecting all or it's safe to unselect all
      setSelected(isUnselectingAll ? [] : countries);
      setFormData(prevFormData => ({
        ...prevFormData,
        cropStageCountryDetail: isUnselectingAll ? [] : countries,
      }));
      return;
    }

    // Handle individual country selection/deselection
    const attemptingToDeselect = selected.length > option.length;
    if (attemptingToDeselect) {
      // Find which country is being deselected
      const countryBeingDeselected = selected.find(
        country => !option.some(opt => opt.countryCode === country.countryCode)
      );
      const findfromsetCropStageDataWithDetails = cropStageDataWithDetails?.cropStageCountryDetail?.find(
        (country) => country.countryCode === countryBeingDeselected?.countryCode
      );
      // Check if it's safe to remove
      if (countryBeingDeselected && findfromsetCropStageDataWithDetails?.isSafeToRemove === false) {
        setSeverity("error");
        setMessage(`This country is associated with one or more modules. Please 
deselect the country from the modules first, then try again`);
        setOpenAlert(true);
        return;
      }
    }
    setSelected(option);
    setFormData((prevFormData) => ({
      ...prevFormData,
      cropStageCountryDetail: option,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setErrors({});
      await validationSchema.validate(formData, { abortEarly: false });

      // Constructing the payload object
      const payload = {
        cropStageName: formData.cropStageName,
        cropStageCountryDetail: selected.map((country) => ({
          createdByUser: user.userName,
          modifiedByUser: user.userName,
          countryName: country.countryName,
          countryCode: country.countryCode,
        })),
        isActive: formData.isActive,
        sequence: sequence || 0,
        createdByUser: user.userName,
        modifiedByUser: user.userName,
        visitCompliance: formData.visitCompliance,
        isMultipleSurvey: formData.isMultipleSurvey,
        isWoodMonitoring: formData.isWoodMonitoring, // Add this new field
        isSubGrowerMonitoring: formData.isSubGrowerMonitoring,
      };

      // Make a POST request using Axios
      const response = await CropStageService.createCropRegistration(payload);

      if (response.status === 201) {
        // Show success snackbar
        navigate("/cropStage");
        setSeverity("success");
        setMessage("Added successfully");
        setOpenAlert(true);
        setLoading(false);
      } else {
        console.error("Failed to submit form");
        // Show error snackbar
        setSeverity("error");
        setMessage("Failed to submit form");
        setOpenAlert(true);
      }
    } catch (error) {
      let newError = {};
      if (error?.name && error.name === "ValidationError") {
        error?.inner?.forEach((err) => {
          newError[err.path] = err.message;
          setErrors(newError || error?.message);
          return;
        });
      } else {
        setSeverity("error");
        setMessage(error?.response?.data?.message || error?.message);
        setOpenAlert(true);
      }
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setErrors({});
      await validationSchema.validate(formData, { abortEarly: false });

      const payload = {
        cropStageUid: id,
        cropStageName: formData.cropStageName,
        cropStageCountryDetail: selected.map((country) => ({
          modifiedByUser: user.userName,
          countryName: country.countryName,
          countryCode: country.countryCode,
          cropStageCountryUid: country.cropStageCountryUid,
        })),
        isActive: formData.isActive,
        sequenceId: sequence,
        modifiedByUser: user.userName,
        visitCompliance: formData.visitCompliance,
        isMultipleSurvey: formData.isMultipleSurvey,
        isWoodMonitoring: formData.isWoodMonitoring, // Add this new field
        isSubGrowerMonitoring: formData.isSubGrowerMonitoring,
      };

      // Make a PUT request using Axios
      const response = await CropStageService.updateCropStageRegistration(
        payload
      );

      if (response.status === 200) {
        // Updated successfully

        navigate("/cropStage");

        // Show success snackbar
        setSeverity("success");
        setMessage("Updated successfully");
        setOpenAlert(true);
        setLoading(false);
      } else {
        console.error("Failed to update form");
        // Show error snackbar
        setSeverity("error");
        setMessage("Failed to update form");
        setOpenAlert(true);
      }
    } catch (error) {
      let newError = {};
      if (error?.name && error.name === "ValidationError") {
        error?.inner?.forEach((err) => {
          newError[err.path] = err.message;
          setErrors(newError || error?.message);
          return;
        });
      } else {
        setSeverity("error");
        setMessage(error?.response?.data?.message || error?.message);
        setOpenAlert(true);
      }
    }
    setLoading(false);
  };

  const handleCheckboxClick = (checkboxName) => {
    if (checkboxName === "visitCompliance" && formData.isMultipleSurvey) {
      setPopupMessage("You must disable Multiple Survey before enabling Visit Compliance.");
      setPopupOpen(true);
    }
    else if (checkboxName === "isMultipleSurvey" && formData.visitCompliance) {
      setPopupMessage("You must disable Visit Compliance before enabling Multiple Survey.");
      setPopupOpen(true);
    }
  };

  // Add this style for disabled checkboxes (at the component level)
  const disabledCheckboxStyle = {
    opacity: 0.2, // This grays out the checkbox when disabled
    pointerEvents: 'none' // This prevents clicking on the disabled checkbox
  };

  return (
    <Layout title="Agri360 ESG Portal">
      <div className="GridItem">
        <div>{id ? "Edit Crop Stage" : "Add Crop Stage"}</div>
        {id && (
          <Box>
            <div className="detailsText">
              Last Modified By :
              <span className="spanDetailsValue">{modifiedBy}</span>
            </div>
            <div className="detailsText">
              Last Modified Date :
              <span className="spanDetailsValue">
                {formatDateDetails(modifiedTime)}
              </span>
            </div>
          </Box>
        )}
      </div>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={1} rowSpacing={4} columnSpacing={2}>
          {/* Crop Stage Name */}
          <Grid
            item
            md={4}
            xs={12}
            sx={{
              display: { md: "flex", xs: "block" },
              alignItems: "center",
              flexDirection: "end",
            }}
          >
            <Tooltip title="Crop Stage Name" placement="top">
              <InputLabel required sx={{
                width: "10rem",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                cursor: 'pointer',
              }} htmlFor="my-input">
                Crop Stage Name
              </InputLabel>
            </Tooltip>
            <Box>
              <TextField
                sx={{
                  width: { xs: "100%", md: "14rem" },
                  marginTop: { xs: "1rem", md: "0" },
                  "& input::placeholder": {
                    fontStyle: "italic",
                  },
                }}
                id="cropStageName"
                value={formData.cropStageName}
                placeholder="Type here .."
                name="cropStageName"
                onChange={handleChange}
                error={!!errors.cropStageName}
                disabled={readOnly || edit}
              />

              {errors.cropStageName && (
                <FormHelperText error={true}>
                  {errors.cropStageName}
                </FormHelperText>
              )}
            </Box>
          </Grid>

          {/* Country */}
          <Grid
            item
            md={4}
            xs={12}
            sx={{
              display: { md: "flex", xs: "block" },
              alignItems: "center",
            }}
          >
            <InputLabel required sx={{ width: "9rem" }} htmlFor="my-input">
              Country
            </InputLabel>
            <FormControl
              sx={{ width: { xs: "100%", md: "14rem" }, minHeight: 50 }}
            >
              <Select
                labelId="mutiple-select-label"
                name="cropStageCountryDetail"
                multiple
                value={selected}
                disabled={readOnly}
                onChange={handleChangeCountry}
                renderValue={(selected) =>
                  selected.map((select) => select.countryName).join(", ")
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                      overflowY: "auto",
                    },
                  },
                }}
              >
                <MenuItem value="all">
                  <ListItemIcon>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={
                        selected.length > 0 &&
                        selected.length < countries.length
                      }
                      icon={<img src={CheckboxUntick} />}
                      checkedIcon={<img src={CheckboxTick} />}
                    />
                  </ListItemIcon>
                  <ListItemText primary="Select All" />
                </MenuItem>
                {countries.map((option) => (
                  <MenuItem key={option.countryName} value={option}>
                    <ListItemIcon>
                      <Checkbox
                        checked={selected.indexOf(option) > -1}
                        icon={<img src={CheckboxUntick} />}
                        checkedIcon={<img src={CheckboxTick} />}
                      />
                    </ListItemIcon>
                    <ListItemText primary={option.countryName} />{" "}
                  </MenuItem>
                ))}
              </Select>
              {errors.cropStageCountryDetail && (
                <FormHelperText error={true}>
                  {errors.cropStageCountryDetail}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Active */}
          <Grid
            item
            md={4}
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputLabel
              sx={{
                margin: "0 2.1rem 0 0rem",  // Match the margin of other labels
                width: "10rem"
              }}
              id="active-label"
            >
              Active
            </InputLabel>
            <Checkbox
              name="isActive"
              disabled={readOnly}
              checked={formData.isActive}
              onChange={(e) => handleChange(e)}
              icon={<img src={CheckboxUntick} />}
              checkedIcon={<img src={CheckboxTick} />}
            />
          </Grid>

          {/* Visit Compliance */}
          <Grid
            item
            md={4}
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputLabel
              sx={{
                margin: "0 2.1rem 0 0rem",
                width: "10rem",
                ...(formData.isMultipleSurvey ? { opacity: 0.5 } : {}) // Apply opacity to label when disabled
              }}
              id="mutiple-select-label"
            >
              Visit Compliance
            </InputLabel>
            <Box onClick={() => formData.isMultipleSurvey && handleCheckboxClick("visitCompliance")}>
              <Checkbox
                name="visitCompliance"
                onChange={handleChange}
                disabled={readOnly || formData.isMultipleSurvey}
                checked={formData.visitCompliance}
                icon={<img src={formData.isMultipleSurvey ? UncheckedDisabled : CheckboxUntick} alt="untick" />}
                checkedIcon={<img src={CheckboxTick} alt="tick" />}
                sx={(readOnly || formData.isMultipleSurvey) ? disabledCheckboxStyle : {}}
              />
            </Box>
          </Grid>

          {/* Add the new Multiple Survey checkbox */}
          <Grid
            item
            md={4}
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputLabel
              sx={{
                margin: "0 2.1rem 0 0rem",
                width: "11rem",
                ...(formData.visitCompliance ? { opacity: 0.5 } : {}) // Apply opacity to label when disabled
              }}
              id="multiple-survey-label"
            >
              Multiple Monitoring
            </InputLabel>
            <Box onClick={() => formData.visitCompliance && handleCheckboxClick("isMultipleSurvey")}>
              <Checkbox
                name="isMultipleSurvey"
                onChange={handleChange}
                disabled={readOnly || formData.visitCompliance}
                checked={formData.isMultipleSurvey}
                icon={<img src={formData.visitCompliance ? UncheckedDisabled : CheckboxUntick} alt="untick" />}
                checkedIcon={<img src={CheckboxTick} alt="tick" />}
                sx={(readOnly || formData.visitCompliance) ? disabledCheckboxStyle : {}}
              />
            </Box>
          </Grid>

          {/* Wood Monitoring Checkbox */}
          <Grid
            item
            md={4}
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputLabel
              sx={{
                margin: "0 2.1rem 0 0rem",  // Match the margin of other labels
                width: "10rem"
              }}
              id="wood-monitoring-label"
            >
              Wood Monitoring
            </InputLabel>
            <Checkbox
              name="isWoodMonitoring"
              onChange={handleChange}
              disabled={readOnly}
              checked={formData.isWoodMonitoring}
              icon={<img src={CheckboxUntick} alt="untick" />}
              checkedIcon={<img src={CheckboxTick} alt="tick" />}
            />
          </Grid>

          {/* Sub Grower Monitoring Checkbox */}
          <Grid
            item
            md={4}
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputLabel
              sx={{
                margin: "0 2.1rem 0 0rem",  // Match the margin of other labels
                width: "10rem"
              }}
              id="sub-grower-monitoring-label"
            >
              Sub Grower Monitoring
            </InputLabel>
            <Checkbox
              name="isSubGrowerMonitoring"
              onChange={handleChange}
              disabled={readOnly}
              checked={formData.isSubGrowerMonitoring}
              icon={<img src={CheckboxUntick} alt="untick" />}
              checkedIcon={<img src={CheckboxTick} alt="tick" />}
            />
          </Grid>
        </Grid>
        <ButtonContainer>
          <CustomButton
            onClick={() => navigate("/cropStage")}
            label="Cancel"
            variant="cancel"
          />
          {edit === true ? (
            <CustomButton
              loading={loading}
              onClick={handleUpdate}
              label="Update"
            />
          ) : (
            <CustomButton
              loading={loading}
              onClick={handleSubmit}
              label=" Submit"
              disabled={readOnly}
            />
          )}
        </ButtonContainer>
      </Paper>

      {/* Add the confirmation dialog */}
      <Modal
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: { xs: "12rem", md: "17.06rem" },
          justifyContent: "center",
          alignItems: "center",
          padding: 4,
          backgroundColor: 'white',
          borderRadius: "12px"
        }}>
          <img src={dangerIcon} />
          <Typography
            sx={{
              fontSize: "16px",
              color: "#000000",
              textAlign: "center",
              marginTop: "-15px",
              marginBottom: "10px"
            }}
            variant="h8"
          >
            {formData.visitCompliance ? (
              <Typography>
                You are not allowed to select Multiple Monitoring when
                <br />
                Visit Compliance is flagged.
              </Typography>
            ) : (
              <Typography>
                You are not allowed to select Visit Compliance when
                <br />
                Multiple Monitoring is flagged.
              </Typography>
            )}

          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              sx={{
                borderRadius: 4,
                minWidth: 90,
                minHeight: 42,
                background: "#EF7D00",
                marginTop: "13px",
                "&:hover": {
                  backgroundColor: "#EF7D00",
                },
              }}
              variant="contained"
              type="button"
              onClick={() => setPopupOpen(false)}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </Layout>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 3rem 0 2rem 0;
`;

export default AddCropStage;
